import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Input, Modal, Select, Space, Table, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
// Api
import { GET_CAMPAIGN_OFFERS } from 'api/campaignV2/queries';
import {
  CLAIM_SOCIAL_BOOST_OFFER,
  DECLINE_SOCIAL_BOOST_OFFER,
  REINSTATE_SOCIAL_BOOST_OFFER,
  SUBMIT_DELIVERABLES_FOR_REVIEW_V2,
} from 'api/campaignV2/mutations';
// Types
import {
  CampaignMediaType,
  CampaignOfferStatus,
  CampaignStatus,
  CBDeliverableItemType,
} from 'api/graphql-global-types';
import {
  ReinstateSocialBoostOffer,
  ReinstateSocialBoostOfferVariables,
} from 'api/campaignV2/types/ReinstateSocialBoostOffer';
// Hooks
import { useUploadCampaignFiles } from 'hooks/useUploadCampaignFiles';
// Ui
import { errorNotification, successNotification } from 'ui/Notification';
import { DetailCard } from 'ui/DetailList/DetailList';
// Components
import ExpiredOfferActions from './components/ExpiredOfferActions';

const { Option } = Select;

// EXPIRED is included so admins can see who claimed a deal even after it
// expired (an expired offer with approvedAt set was claimed first).
const DEFAULT_STATUSES = [
  CampaignOfferStatus.INVITED,
  CampaignOfferStatus.ACCEPTED,
  CampaignOfferStatus.EXPIRED,
];

// The status dropdown maps to the statusesV2 filter. 'ALL' keeps the
// invited, claimed and expired offers this panel operates on.
const STATUS_FILTER_OPTIONS: {
  label: string;
  value: string;
  statuses: CampaignOfferStatus[];
}[] = [
  {
    label: 'Invited, Claimed & Expired',
    value: 'ALL',
    statuses: DEFAULT_STATUSES,
  },
  {
    label: 'Invited',
    value: CampaignOfferStatus.INVITED,
    statuses: [CampaignOfferStatus.INVITED],
  },
  {
    label: 'Claimed',
    value: CampaignOfferStatus.ACCEPTED,
    statuses: [CampaignOfferStatus.ACCEPTED],
  },
  {
    label: 'Expired',
    value: CampaignOfferStatus.EXPIRED,
    statuses: [CampaignOfferStatus.EXPIRED],
  },
  {
    // "Verified" is the brand-facing name for a share the brand has approved
    // via reviewDeliverables, which moves the offer to COMPLETED.
    label: 'Verified',
    value: CampaignOfferStatus.COMPLETED,
    statuses: [CampaignOfferStatus.COMPLETED],
  },
];

// Social-boost naming: ACCEPTED reads as "Claimed", COMPLETED as "Verified".
// An expired offer that has approvedAt was claimed before it expired.
const offerStatusLabel = (record: {
  statusV2: CampaignOfferStatus;
  approvedAt?: string | null;
}): string => {
  switch (record.statusV2) {
    case CampaignOfferStatus.INVITED:
      return 'Invited';
    case CampaignOfferStatus.ACCEPTED:
      return 'Claimed';
    case CampaignOfferStatus.COMPLETED:
      return 'Verified';
    case CampaignOfferStatus.EXPIRED:
      return record.approvedAt ? 'Expired (Claimed)' : 'Expired';
    default:
      return record.statusV2;
  }
};

const DEFAULT_PAGE_SIZE = 10;

type SocialBoostTalentOffersProps = {
  campaignId: string;
  campaignStatus: CampaignStatus;
  refetchCampaign: () => void;
};

// Admin performs the whole talent-facing pre-review flow on behalf of the
// athlete: claim / decline the invite (INVITED), then submit the proof — post
// URL + screenshot — for review (ACCEPTED). Both statuses live in one table so
// a claimed invite immediately becomes submittable after refetch. Rejected
// shares (disputes) are handled by the separate dispute-resolution panel.
const SocialBoostTalentOffersComponent = ({
  campaignId,
  campaignStatus,
  refetchCampaign,
}: SocialBoostTalentOffersProps): JSX.Element => {
  // Pagination + filter state (server-side; the campaign can have hundreds of
  // offers so we never fetch them all at once).
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [statusValue, setStatusValue] = useState<string>('ALL');
  const [nameFilter, setNameFilter] = useState<string>('');

  const statuses =
    STATUS_FILTER_OPTIONS.find((option) => option.value === statusValue)
      ?.statuses ?? DEFAULT_STATUSES;

  const { data, loading, refetch } = useQuery(GET_CAMPAIGN_OFFERS, {
    variables: {
      input: {
        campaignId,
        statusesV2: statuses,
        limit: pageSize,
        offset: (page - 1) * pageSize,
        ...(nameFilter.trim() && { name: nameFilter.trim() }),
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const handleStatusChange = (value: string) => {
    setStatusValue(value);
    setPage(1);
  };

  const handleNameSearch = (value: string) => {
    setNameFilter(value);
    setPage(1);
  };

  const { createCampaignDeliverableFiles, uploadDeliverables } =
    useUploadCampaignFiles();

  const [claimSocialBoostOffer, { loading: claiming }] = useMutation(
    CLAIM_SOCIAL_BOOST_OFFER,
    {
      onCompleted: () => {
        successNotification('Invite claimed on behalf of talent');
        refetch();
      },
      onError: (error) => errorNotification(error.message),
    }
  );

  const [declineSocialBoostOffer, { loading: declining }] = useMutation(
    DECLINE_SOCIAL_BOOST_OFFER,
    {
      onCompleted: () => {
        successNotification('Invite declined on behalf of talent');
        refetch();
      },
      onError: (error) => errorNotification(error.message),
    }
  );

  const [reinstateSocialBoostOffer, { loading: reinstating }] = useMutation<
    ReinstateSocialBoostOffer,
    ReinstateSocialBoostOfferVariables
  >(REINSTATE_SOCIAL_BOOST_OFFER, {
    onCompleted: (result) => {
      successNotification(
        result.reinstateSocialBoostOffer.statusV2 ===
          CampaignOfferStatus.INVITED
          ? 'Talent re-invited'
          : 'Offer marked as claimed on behalf of talent'
      );
      refetch();
      // Reinstating changes the campaign budget and budget-fulfilled state.
      refetchCampaign();
    },
    onError: (error) => errorNotification(error.message),
  });

  const [submitDeliverablesForReviewV2] = useMutation(
    SUBMIT_DELIVERABLES_FOR_REVIEW_V2,
    {
      onCompleted: () => {
        successNotification('Proof submitted on behalf of talent');
        setActiveOffer(null);
        setPostUrl('');
        setScreenshot(null);
        refetch();
      },
      onError: (error) => errorNotification(error.message),
    }
  );

  // Submit-proof modal state
  const [activeOffer, setActiveOffer] = useState<any | null>(null);
  const [postUrl, setPostUrl] = useState<string>('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const handleClaim = (offerId: string) => {
    claimSocialBoostOffer({ variables: { input: { offerId } } });
  };

  const handleDecline = (offerId: string) => {
    declineSocialBoostOffer({ variables: { input: { offerId } } });
  };

  const handleReinstate = (offerId: string, status: CampaignOfferStatus) => {
    reinstateSocialBoostOffer({ variables: { input: { offerId, status } } });
  };

  const closeSubmitModal = () => {
    setActiveOffer(null);
    setPostUrl('');
    setScreenshot(null);
  };

  const handleSubmitProof = async () => {
    if (!activeOffer) return;

    // On claim the backend creates a single Social Boost deliverable holding a
    // LINK item (the post URL) and a FILES item (the screenshot).
    const deliverable = activeOffer.deliverablesV2?.[0];
    const linkItem = deliverable?.items?.find(
      (item: any) => item.type === CBDeliverableItemType.LINK
    );
    const filesItem = deliverable?.items?.find(
      (item: any) => item.type === CBDeliverableItemType.FILES
    );

    if (!linkItem || !filesItem) {
      errorNotification('No deliverable items found for this offer');
      return;
    }
    if (!postUrl.trim()) {
      errorNotification('Post URL is required');
      return;
    }
    if (!screenshot) {
      errorNotification('Screenshot is required');
      return;
    }

    try {
      setSubmitting(true);

      // Presign + upload the screenshot to S3.
      const { data: presignData } = await createCampaignDeliverableFiles({
        variables: { input: { files: [{ name: screenshot.name }] } },
      });
      const presignedUrls = presignData?.createCampaignDeliverableFiles ?? [];
      const uploaded = await uploadDeliverables(presignedUrls, [screenshot]);
      const key = uploaded[0]?.key;

      if (!key) {
        errorNotification('Screenshot upload failed');
        return;
      }

      await submitDeliverablesForReviewV2({
        variables: {
          input: {
            offerId: activeOffer.id,
            items: [
              {
                deliverableItemId: filesItem.id,
                type: CBDeliverableItemType.FILES,
                files: [
                  {
                    key,
                    name: screenshot.name,
                    type: CampaignMediaType.IMAGE,
                  },
                ],
              },
              {
                deliverableItemId: linkItem.id,
                type: CBDeliverableItemType.LINK,
                link: postUrl.trim(),
              },
            ],
          },
        },
      });
    } catch (error) {
      errorNotification((error as Error)?.message);
    } finally {
      setSubmitting(false);
    }
  };

  const offers = data?.getCampaignOffers?.entities ?? [];
  const total = data?.getCampaignOffers?.total ?? 0;

  const columns = [
    {
      title: 'Talent',
      key: 'talent',
      render: (record: any) =>
        record.profileName ||
        `${record.firstName ?? ''} ${record.lastName ?? ''}`.trim() ||
        '—',
    },
    {
      title: 'Status',
      key: 'statusV2',
      render: (record: any) => offerStatusLabel(record),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: any) => {
        if (record.statusV2 === CampaignOfferStatus.INVITED) {
          return (
            <Space>
              <Button
                type="primary"
                size="small"
                loading={claiming}
                onClick={() => handleClaim(record.id)}
              >
                Claim
              </Button>
              <Button
                danger
                size="small"
                loading={declining}
                onClick={() => handleDecline(record.id)}
              >
                Decline
              </Button>
            </Space>
          );
        }

        if (record.statusV2 === CampaignOfferStatus.ACCEPTED) {
          return (
            <Button
              type="primary"
              size="small"
              onClick={() => setActiveOffer(record)}
            >
              Submit Proof
            </Button>
          );
        }

        if (record.statusV2 === CampaignOfferStatus.EXPIRED) {
          return (
            <ExpiredOfferActions
              campaignStatus={campaignStatus}
              loading={reinstating}
              offerId={record.id}
              onReinstate={handleReinstate}
            />
          );
        }

        return null;
      },
    },
  ];

  return (
    <DetailCard title="Social Boost talent offers (on behalf)">
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          value={statusValue}
          onChange={handleStatusChange}
          style={{ width: 200 }}
        >
          {STATUS_FILTER_OPTIONS.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
        <Input.Search
          placeholder="Search talent name"
          allowClear
          onSearch={handleNameSearch}
          style={{ width: 260 }}
        />
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={offers}
        columns={columns}
        locale={{ emptyText: 'No offers for the selected status' }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize ?? DEFAULT_PAGE_SIZE);
          },
        }}
      />

      <Modal
        title="Submit proof on behalf of talent"
        visible={!!activeOffer}
        onCancel={closeSubmitModal}
        onOk={handleSubmitProof}
        okText="Submit"
        confirmLoading={submitting}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            placeholder="Post URL"
            value={postUrl}
            onChange={(e) => setPostUrl(e.target.value)}
          />
          <Upload
            accept="image/*"
            maxCount={1}
            beforeUpload={(file) => {
              setScreenshot(file);
              return false;
            }}
            onRemove={() => setScreenshot(null)}
            fileList={
              screenshot ? [{ uid: '-1', name: screenshot.name } as any] : []
            }
          >
            <Button icon={<UploadOutlined />}>Select screenshot</Button>
          </Upload>
        </Space>
      </Modal>
    </DetailCard>
  );
};

export default SocialBoostTalentOffersComponent;
